import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Config-only context for the embedded job-docs-client.
 *
 * Holds `{ enabled, clientUrl }` and nothing else — no route knowledge, no
 * URL builders, no effects. The host must not know the embedded app's routes;
 * `useJobDocsIframe` derives the single grid path from this config and the
 * embedded app owns everything past that.
 */
const JobDocsConfigContext = createContext({
  enabled: false,
  clientUrl: undefined,
});

const JobDocsConfigProvider = ({ enabled = false, clientUrl, children }) => {
  const value = useMemo(() => {
    return {
      // Only a literal `true` enables — the gate (lib/jobDocsGate) already
      // coerces, but the provider honors the boolean contract regardless.
      enabled: enabled === true,
      clientUrl,
    };
  }, [enabled, clientUrl]);

  return (
    <JobDocsConfigContext.Provider value={value}>
      {children}
    </JobDocsConfigContext.Provider>
  );
};

JobDocsConfigProvider.propTypes = {
  /** Resolved feature gate (LD flag AND env default AND MES settings). */
  enabled: PropTypes.bool,
  /** Origin of the job-docs-client (`JOB_DOCS_CLIENT_URL`). */
  clientUrl: PropTypes.string,
  children: PropTypes.node.isRequired,
};

/**
 * @returns {{ enabled: boolean, clientUrl: string|undefined }}
 */
function useJobDocsConfig() {
  return useContext(JobDocsConfigContext);
}

export { JobDocsConfigProvider, useJobDocsConfig };
